/**
 * Browse My documents + extension icon rail prototype
 *
 * Vision mock for ADR-0019 / bet 2026-07-23-browse-my-documents-and-extension-icon-rail:
 * - Icon rail: Browse, Inbox, Flows, Drive, Basecamp (Basecamp gated by active workspace enablement)
 * - Browse under workspace: My documents | Pinned | Documents (no Basecamp nest)
 * - My documents: expand desk tree + desk landing main pane (template, not App Home)
 *
 * Access: http://flownotes.localhost/?test=browse-desk-sidebar
 */

import { useEffect, useMemo, useState } from 'react'
import {
  Home,
  Inbox,
  Zap,
  FolderOpen,
  Folder,
  FileText,
  Pin,
  ChevronDown,
  ChevronRight,
  Lock,
  Sparkles,
  Plus,
  Settings2,
  User,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { testIds } from '@/lib/test-ids'
import { BasecampIcon } from '@/components/icons/basecamp-icon'
import { GoogleDriveIcon } from '@/components/icons/google-drive-icon'

type RailMode = 'browse' | 'inbox' | 'flows' | 'drive' | 'basecamp'
type MainView = 'desk-landing' | 'documents-root' | 'basecamp-panel' | 'drive-panel' | 'empty'

type WorkspaceMock = {
  id: string
  name: string
  color: string
  basecampEnabled: boolean
  driveEnabled: boolean
  deskSlug: string
  deskLabel: string
}

const WORKSPACES: WorkspaceMock[] = [
  {
    id: 'hop',
    name: 'Hop Studios',
    color: '#c2410c',
    basecampEnabled: true,
    driveEnabled: true,
    deskSlug: 'rowan',
    deskLabel: 'Rowan',
  },
  {
    id: 'personal',
    name: 'Personal',
    color: '#57534e',
    basecampEnabled: false,
    driveEnabled: true,
    deskSlug: 'rowan',
    deskLabel: 'Rowan',
  },
]

const DESK_TREE = [
  { id: 'private', label: 'private', locked: true },
  { id: 'meetings', label: 'meetings', locked: false },
  { id: 'soul', label: '.soul', locked: true },
  { id: 'notes', label: 'notes', locked: false },
] as const

const DESK_RECENT = [
  { title: 'Weekly priorities', path: 'notes/weekly.md', when: '2h ago' },
  { title: 'Private / hiring notes', path: 'private/hiring-scratch.md', when: 'Yesterday' },
  { title: 'TJ review prep', path: 'meetings/2026-07-22-rdek.md', when: '2d ago' },
]

export function BrowseDeskSidebarPrototype() {
  const [activeWorkspaceIds, setActiveWorkspaceIds] = useState<string[]>(['hop'])
  const [rail, setRail] = useState<RailMode>('browse')
  const [main, setMain] = useState<MainView>('desk-landing')
  const [myDocsOpen, setMyDocsOpen] = useState(true)
  const [docsOpen, setDocsOpen] = useState(false)
  const [pinnedOpen, setPinnedOpen] = useState(false)

  const activeWorkspaces = useMemo(
    () => WORKSPACES.filter((w) => activeWorkspaceIds.includes(w.id)),
    [activeWorkspaceIds]
  )

  const showBasecampRail = activeWorkspaces.some((w) => w.basecampEnabled)
  const showDriveRail = activeWorkspaces.some((w) => w.driveEnabled)

  const toggleWorkspace = (id: string) => {
    setActiveWorkspaceIds((prev) => {
      if (prev.includes(id)) {
        if (prev.length === 1) return prev
        return prev.filter((x) => x !== id)
      }
      return [...prev, id]
    })
  }

  useEffect(() => {
    if (rail === 'basecamp' && !showBasecampRail) {
      setRail('browse')
      setMain('desk-landing')
    }
  }, [rail, showBasecampRail])

  const hop = WORKSPACES[0]

  return (
    <div
      className="flex h-screen bg-background text-foreground"
      data-testid={testIds.browseDeskSidebarTest.root}
    >
      {/* Icon rail */}
      <aside
        className="flex w-12 shrink-0 flex-col items-center gap-1 border-r border-border bg-muted/40 py-3"
        data-testid={testIds.browseDeskSidebarTest.iconRail}
      >
        <RailButton
          label="Browse"
          active={rail === 'browse'}
          onClick={() => {
            setRail('browse')
            setMain('desk-landing')
          }}
          testId={testIds.browseDeskSidebarTest.railBrowse}
        >
          <FolderOpen className="h-4 w-4" />
        </RailButton>
        <RailButton
          label="Inbox"
          active={rail === 'inbox'}
          onClick={() => {
            setRail('inbox')
            setMain('empty')
          }}
        >
          <Inbox className="h-4 w-4" />
        </RailButton>
        <RailButton
          label="Flows"
          active={rail === 'flows'}
          onClick={() => {
            setRail('flows')
            setMain('empty')
          }}
        >
          <Zap className="h-4 w-4" />
        </RailButton>
        <div className="my-2 h-px w-6 bg-border" />
        {showDriveRail && (
          <RailButton
            label="Google Drive"
            active={rail === 'drive'}
            onClick={() => {
              setRail('drive')
              setMain('drive-panel')
            }}
            testId={testIds.browseDeskSidebarTest.railDrive}
          >
            <GoogleDriveIcon className="h-4 w-4" />
          </RailButton>
        )}
        {showBasecampRail && (
          <RailButton
            label="Basecamp"
            active={rail === 'basecamp'}
            onClick={() => {
              setRail('basecamp')
              setMain('basecamp-panel')
            }}
            testId={testIds.browseDeskSidebarTest.railBasecamp}
            brandMark
          >
            <BasecampIcon className="h-4 w-4" />
          </RailButton>
        )}
      </aside>

      {/* Mode panel */}
      <div className="flex w-72 shrink-0 flex-col border-r border-border bg-background">
        <div className="flex items-center justify-between border-b border-border px-3 py-2">
          <span className="text-xs font-semibold tracking-wide text-muted-foreground">
            {rail === 'browse'
              ? 'Browse'
              : rail === 'basecamp'
                ? 'Basecamp'
                : rail === 'drive'
                  ? 'Google Drive'
                  : rail}
          </span>
          <a
            href="/?test=index"
            className="text-[10px] text-muted-foreground underline-offset-2 hover:underline"
          >
            Prototypes
          </a>
        </div>

        {rail === 'browse' && (
          <div className="flex-1 overflow-y-auto px-2 py-2" data-testid={testIds.browseDeskSidebarTest.browsePanel}>
            <button
              type="button"
              className="mb-2 flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-sm text-muted-foreground hover:bg-muted"
            >
              <Home className="h-3.5 w-3.5" />
              Home
              <span className="ml-auto text-[10px] text-muted-foreground/80">App Home /</span>
            </button>

            <p className="mb-1 px-2 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
              Workspaces
            </p>
            <div className="mb-3 space-y-1 px-1">
              {WORKSPACES.map((ws) => {
                const on = activeWorkspaceIds.includes(ws.id)
                return (
                  <button
                    key={ws.id}
                    type="button"
                    onClick={() => toggleWorkspace(ws.id)}
                    className={cn(
                      'flex w-full items-center gap-2 rounded-md px-2 py-1 text-xs',
                      on ? 'bg-muted font-medium' : 'text-muted-foreground hover:bg-muted/60'
                    )}
                    data-testid={testIds.browseDeskSidebarTest.workspaceToggle(ws.id)}
                  >
                    <span
                      className="h-2 w-2 rounded-full"
                      style={{ backgroundColor: on ? ws.color : '#a8a29e' }}
                    />
                    {ws.name}
                    <span className="ml-auto text-[10px] opacity-70">
                      {ws.basecampEnabled ? 'BC' : ''}
                      {ws.driveEnabled ? ' Dr' : ''}
                    </span>
                  </button>
                )
              })}
              <p className="px-2 pt-1 text-[10px] leading-snug text-muted-foreground">
                Toggle Personal off to hide Basecamp rail (Rule 4: icon only if an active workspace enables it).
              </p>
            </div>

            {activeWorkspaces.map((ws) => (
              <div key={ws.id} className="mb-4">
                <div className="mb-1 flex items-center gap-2 px-2 text-sm font-semibold">
                  <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: ws.color }} />
                  {ws.name}
                </div>

                {/* My documents */}
                <div className="ml-1">
                  <button
                    type="button"
                    className={cn(
                      'flex w-full items-center gap-1.5 rounded-md px-2 py-1.5 text-sm',
                      main === 'desk-landing' && rail === 'browse'
                        ? 'bg-primary/10 text-foreground'
                        : 'hover:bg-muted'
                    )}
                    onClick={() => {
                      setMyDocsOpen(true)
                      setMain('desk-landing')
                    }}
                    data-testid={testIds.browseDeskSidebarTest.myDocumentsRow}
                  >
                    {myDocsOpen ? (
                      <ChevronDown className="h-3.5 w-3.5 shrink-0" />
                    ) : (
                      <ChevronRight className="h-3.5 w-3.5 shrink-0" />
                    )}
                    <User className="h-3.5 w-3.5 shrink-0 text-primary" />
                    <span className="font-medium">My documents</span>
                  </button>
                  {myDocsOpen && (
                    <div className="ml-6 space-y-0.5 border-l border-border pl-2">
                      {DESK_TREE.map((node) => (
                        <div
                          key={node.id}
                          className="flex items-center gap-1.5 rounded px-1.5 py-1 text-xs text-muted-foreground"
                        >
                          {node.locked ? (
                            <Lock className="h-3 w-3" />
                          ) : (
                            <Folder className="h-3 w-3" />
                          )}
                          {node.label}
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Pinned */}
                <button
                  type="button"
                  className="mt-0.5 flex w-full items-center gap-1.5 rounded-md px-2 py-1.5 text-sm hover:bg-muted"
                  onClick={() => setPinnedOpen((v) => !v)}
                >
                  {pinnedOpen ? (
                    <ChevronDown className="h-3.5 w-3.5" />
                  ) : (
                    <ChevronRight className="h-3.5 w-3.5" />
                  )}
                  <Pin className="h-3.5 w-3.5" />
                  Pinned
                </button>
                {pinnedOpen && (
                  <p className="ml-8 py-1 text-[10px] text-muted-foreground">No pins in harness</p>
                )}

                {/* Documents */}
                <button
                  type="button"
                  className={cn(
                    'mt-0.5 flex w-full items-center gap-1.5 rounded-md px-2 py-1.5 text-sm',
                    main === 'documents-root' ? 'bg-muted' : 'hover:bg-muted'
                  )}
                  onClick={() => {
                    setDocsOpen(true)
                    setMain('documents-root')
                  }}
                  data-testid={testIds.browseDeskSidebarTest.documentsRow}
                >
                  {docsOpen ? (
                    <ChevronDown className="h-3.5 w-3.5" />
                  ) : (
                    <ChevronRight className="h-3.5 w-3.5" />
                  )}
                  <FolderOpen className="h-3.5 w-3.5" />
                  Documents
                </button>
                {docsOpen && (
                  <div className="ml-6 space-y-0.5 border-l border-border pl-2 text-xs text-muted-foreground">
                    <div className="flex items-center gap-1.5 px-1.5 py-1">
                      <Folder className="h-3 w-3" />
                      clients
                    </div>
                    <div className="flex items-center gap-1.5 px-1.5 py-1">
                      <Folder className="h-3 w-3" />
                      users
                    </div>
                    <div className="flex items-center gap-1.5 px-1.5 py-1">
                      <Folder className="h-3 w-3" />
                      platform
                    </div>
                    <p className="px-1.5 py-1 text-[10px] italic">
                      Full org tree - Basecamp is not here
                    </p>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {rail === 'basecamp' && (
          <div className="flex-1 space-y-1 p-2" data-testid={testIds.browseDeskSidebarTest.basecampPanel}>
            <p className="px-2 text-[10px] text-muted-foreground">
              Icon-rail panel (shared primitives). Grouped by workspace when multiple have BC.
            </p>
            {activeWorkspaces
              .filter((w) => w.basecampEnabled)
              .map((ws) => (
                <div key={ws.id} className="rounded-md border border-border p-2">
                  <div className="mb-1 flex items-center gap-2 text-xs font-semibold">
                    <span className="h-2 w-2 rounded-full" style={{ backgroundColor: ws.color }} />
                    {ws.name}
                  </div>
                  {['Bookmarks', 'Todos', 'Projects', 'Activity'].map((label) => (
                    <button
                      key={label}
                      type="button"
                      className="flex w-full rounded px-2 py-1 text-sm hover:bg-muted"
                    >
                      {label}
                    </button>
                  ))}
                </div>
              ))}
          </div>
        )}

        {rail === 'drive' && (
          <div className="p-3 text-sm text-muted-foreground">
            Drive panel placeholder (production already on rail).
          </div>
        )}

        {(rail === 'inbox' || rail === 'flows') && (
          <div className="p-3 text-sm text-muted-foreground">Peer mode placeholder.</div>
        )}
      </div>

      {/* Main */}
      <main className="flex min-w-0 flex-1 flex-col" data-testid={testIds.browseDeskSidebarTest.mainPane}>
        {main === 'desk-landing' && (
          <DeskLanding
            workspaceName={hop.name}
            deskSlug={hop.deskSlug}
            deskLabel={hop.deskLabel}
          />
        )}
        {main === 'documents-root' && (
          <div className="p-8">
            <h1 className="text-xl font-semibold">Documents</h1>
            <p className="mt-2 max-w-lg text-sm text-muted-foreground">
              Full workspace tree root - same open-intent family as today. Org corpus lives here;
              personal desk is under My documents.
            </p>
          </div>
        )}
        {main === 'basecamp-panel' && (
          <div className="p-8">
            <h1 className="text-xl font-semibold">Basecamp</h1>
            <p className="mt-2 max-w-lg text-sm text-muted-foreground">
              Standalone lens on the icon rail (alongside Drive). Not nested under Browse.
            </p>
          </div>
        )}
        {main === 'drive-panel' && (
          <div className="p-8">
            <h1 className="text-xl font-semibold">Google Drive</h1>
            <p className="mt-2 text-sm text-muted-foreground">Existing production surface.</p>
          </div>
        )}
        {main === 'empty' && (
          <div className="flex flex-1 items-center justify-center text-sm text-muted-foreground">
            Select Browse or an extension on the rail
          </div>
        )}
      </main>
    </div>
  )
}

function RailButton({
  label,
  active,
  onClick,
  children,
  testId,
  brandMark = false,
}: {
  label: string
  active: boolean
  onClick: () => void
  children: React.ReactNode
  testId?: string
  brandMark?: boolean
}) {
  return (
    <button
      type="button"
      title={label}
      aria-label={label}
      aria-pressed={active}
      onClick={onClick}
      data-testid={testId}
      className={cn(
        'group flex h-9 w-9 items-center justify-center rounded-md transition-colors',
        active
          ? 'bg-primary/15 text-primary ring-1 ring-primary/40'
          : 'text-muted-foreground hover:bg-muted hover:text-foreground',
        brandMark && !active && '[&>svg]:grayscale [&>svg]:transition-[filter] [&>svg]:duration-150 group-hover:[&>svg]:grayscale-0',
      )}
    >
      {children}
    </button>
  )
}

function DeskLanding({
  workspaceName,
  deskSlug,
  deskLabel,
}: {
  workspaceName: string
  deskSlug: string
  deskLabel: string
}) {
  return (
    <div className="flex-1 overflow-y-auto" data-testid={testIds.browseDeskSidebarTest.deskLanding}>
      <div className="border-b border-border bg-muted/30 px-8 py-6">
        <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
          {workspaceName} · Workspace Desk
        </p>
        <h1 className="mt-1 text-2xl font-semibold tracking-tight">
          My documents
          <span className="ml-2 text-base font-normal text-muted-foreground">
            {deskLabel}
          </span>
        </h1>
        <p className="mt-2 max-w-xl text-sm text-muted-foreground">
          Your desk in this workspace - drafts, private notes, and role soul. Not App Home, and not
          Account Space (cross-workspace identity stays unbuilt).
        </p>
        <p className="mt-1 font-mono text-[11px] text-muted-foreground/80">
          users/{deskSlug}/
        </p>
        <div className="mt-4 flex flex-wrap gap-2">
          <button
            type="button"
            className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground"
          >
            <Plus className="h-3.5 w-3.5" />
            New note on desk
          </button>
          <button
            type="button"
            className="inline-flex items-center gap-1.5 rounded-md border border-border bg-background px-3 py-1.5 text-sm"
          >
            <Sparkles className="h-3.5 w-3.5" />
            Open .soul/preferences
          </button>
        </div>
      </div>

      <div className="grid gap-6 px-8 py-6 lg:grid-cols-2">
        <section className="rounded-lg border border-border p-4">
          <div className="mb-3 flex items-center gap-2">
            <Lock className="h-4 w-4 text-primary" />
            <h2 className="text-sm font-semibold">Private</h2>
          </div>
          <p className="mb-3 text-xs text-muted-foreground">
            Owner + workspace admins only. Sensitive drafts land here by default when you need
            privacy.
          </p>
          <button
            type="button"
            className="text-sm text-primary underline-offset-2 hover:underline"
          >
            Open private/
          </button>
        </section>

        <section className="rounded-lg border border-border p-4">
          <div className="mb-3 flex items-center gap-2">
            <Settings2 className="h-4 w-4" />
            <h2 className="text-sm font-semibold">Desk shortcuts</h2>
          </div>
          <ul className="space-y-2 text-sm">
            <li className="flex items-center gap-2 text-muted-foreground">
              <Folder className="h-3.5 w-3.5" /> meetings/
            </li>
            <li className="flex items-center gap-2 text-muted-foreground">
              <Folder className="h-3.5 w-3.5" /> notes/
            </li>
            <li className="flex items-center gap-2 text-muted-foreground">
              <Lock className="h-3.5 w-3.5" /> .soul/ (agent identity here)
            </li>
          </ul>
        </section>

        <section className="rounded-lg border border-border p-4 lg:col-span-2">
          <div className="mb-3 flex items-center gap-2">
            <FileText className="h-4 w-4" />
            <h2 className="text-sm font-semibold">Recent on this desk</h2>
          </div>
          <ul className="divide-y divide-border">
            {DESK_RECENT.map((row) => (
              <li
                key={row.path}
                className="flex items-center justify-between py-2.5 text-sm first:pt-0"
              >
                <div>
                  <div className="font-medium">{row.title}</div>
                  <div className="font-mono text-[11px] text-muted-foreground">{row.path}</div>
                </div>
                <span className="text-xs text-muted-foreground">{row.when}</span>
              </li>
            ))}
          </ul>
          <p className="mt-3 text-[11px] text-muted-foreground">
            Template seed later: optional desk README / kind:dashboard shortcodes. v1 can ship with
            this code layout + private/README already provisioned.
          </p>
        </section>
      </div>
    </div>
  )
}

export default BrowseDeskSidebarPrototype
